import { useEffect, useState } from "react";
import {
  Stack,
  Grid,
  Card,
  Box,
  Typography,
  Button,
  CircularProgress,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  Divider,
  TextField,
  MenuItem,
  Alert,
  Paper,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { useSnackbar } from "notistack";
import Iconify from "../iconify";
import { FinanceService } from "../../api/finance.service";
import { fCurrency } from "../../utils/formatNumber";
import { fDate, fDateTime } from "../../utils/formatTime";
import Scrollbar from "../scrollbar/Scrollbar";
import { generatePlPdf } from "../../utils/plReportPdf";

// ── Quarter options (same as VatSummaryPanel) ─────────────────────────────────
const currentYear = new Date().getFullYear();
const QUARTER_OPTIONS = Array.from({ length: 8 }, (_, i) => {
  const totalQ = currentYear * 4 + 3 - i;
  const y = Math.floor(totalQ / 4);
  const q = (totalQ % 4) + 1;
  return { value: `Q${q}_${y}`, label: `Q${q} ${y}` };
});

// ── Report card ────────────────────────────────────────────────────────────────
function ReportCard({
  title,
  description,
  icon,
  color,
  children,
}: {
  title: string;
  description: string;
  icon: string;
  color: string;
  children: React.ReactNode;
}) {
  return (
    <Card variant="outlined" sx={{ p: 2.5, borderRadius: 1.5 }}>
      <Stack direction="row" alignItems="center" spacing={1.5} mb={1.5}>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: 1.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: `${color}.lighter`,
            color: `${color}.main`,
            flexShrink: 0,
          }}>
          <Iconify icon={icon} width={20} />
        </Box>
        <Box>
          <Typography variant="subtitle2">{title}</Typography>
          <Typography variant="caption" color="text.secondary">
            {description}
          </Typography>
        </Box>
      </Stack>
      <Divider sx={{ mb: 1.5 }} />
      {children}
    </Card>
  );
}

// ── P&L Preview ───────────────────────────────────────────────────────────────
interface PlPreview {
  periodFrom: string;
  periodTo: string;
  totalIncome: number;
  totalExpenditure: number;
  netProfit: number;
  grossProfitMargin: string;
  income: any[];
  expenditure: any[];
}

function PlReportCard() {
  const { enqueueSnackbar } = useSnackbar();
  const [dateFrom, setDateFrom] = useState<Date | null>(null);
  const [dateTo, setDateTo] = useState<Date | null>(null);
  const [preview, setPreview] = useState<PlPreview | null>(null);
  const [loading, setLoading] = useState(false);

  const handlePreview = async () => {
    if (!dateFrom || !dateTo) return;
    try {
      setLoading(true);
      const res = await new FinanceService().generatePlReport({
        dateFrom: dateFrom.toISOString().split("T")[0],
        dateTo: dateTo.toISOString().split("T")[0],
      });
      setPreview(res.data);
    } catch {
      enqueueSnackbar("Failed to generate P&L preview", { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  const canPreview = dateFrom && dateTo;

  return (
    <ReportCard
      title="Profit & Loss Statement"
      description="Income vs expenditure by category and period"
      icon="eva:trending-up-fill"
      color="info">
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <Stack direction="row" spacing={1.5} mb={1.5}>
          <DatePicker
            label="From"
            value={dateFrom}
            onChange={setDateFrom}
            slotProps={{ textField: { size: "small", fullWidth: true } }}
          />
          <DatePicker
            label="To"
            value={dateTo}
            minDate={dateFrom ?? undefined}
            onChange={setDateTo}
            slotProps={{ textField: { size: "small", fullWidth: true } }}
          />
        </Stack>
      </LocalizationProvider>

      <Button
        variant="contained"
        color="info"
        fullWidth
        disabled={!canPreview || loading}
        startIcon={
          loading ? (
            <CircularProgress size={14} color="inherit" />
          ) : (
            <Iconify icon="eva:eye-fill" />
          )
        }
        onClick={handlePreview}
        sx={{ mb: preview ? 2 : 0 }}>
        {loading ? "Calculating…" : "Preview P&L"}
      </Button>

      {/* Live preview */}
      {preview && (
        <Stack spacing={1}>
          <Divider />
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="caption" color="text.secondary">
              Total income
            </Typography>
            <Typography
              variant="caption"
              fontFamily="monospace"
              color="success.dark"
              fontWeight={600}>
              {fCurrency(preview.totalIncome)}
            </Typography>
          </Stack>
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="caption" color="text.secondary">
              Total expenditure
            </Typography>
            <Typography
              variant="caption"
              fontFamily="monospace"
              color="error.dark"
              fontWeight={600}>
              {fCurrency(preview.totalExpenditure)}
            </Typography>
          </Stack>
          <Divider />
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="caption" fontWeight={600}>
              Net profit
            </Typography>
            <Typography
              variant="caption"
              fontFamily="monospace"
              fontWeight={700}
              color={preview.netProfit >= 0 ? "success.dark" : "error.dark"}>
              {fCurrency(preview.netProfit)}
            </Typography>
          </Stack>
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="caption" color="text.secondary">
              Gross margin
            </Typography>
            <Typography variant="caption" fontFamily="monospace">
              {preview.grossProfitMargin}
            </Typography>
          </Stack>

          {/* Income breakdown */}
          {preview.income.length > 0 && (
            <>
              <Typography
                variant="caption"
                color="text.secondary"
                fontWeight={600}
                sx={{ mt: 0.5 }}>
                Income breakdown
              </Typography>
              {preview.income.map((row: any) => (
                <Stack
                  key={row.categoryName}
                  direction="row"
                  justifyContent="space-between">
                  <Typography variant="caption" color="text.secondary">
                    {row.categoryName}
                  </Typography>
                  <Typography
                    variant="caption"
                    fontFamily="monospace"
                    color="success.dark">
                    {fCurrency(parseFloat(row.totalIncome))}
                  </Typography>
                </Stack>
              ))}
            </>
          )}

          <Button
            variant="outlined"
            color="info"
            size="small"
            fullWidth
            sx={{ mt: 1 }}
            startIcon={<Iconify icon="eva:download-fill" />}
            onClick={() => generatePlPdf(preview)}>
            Download PDF
          </Button>
        </Stack>
      )}
    </ReportCard>
  );
}

// ── VAT100 card ────────────────────────────────────────────────────────────────
function Vat100Card({ onGenerateVat100 }: { onGenerateVat100: () => void }) {
  const { enqueueSnackbar } = useSnackbar();
  const [quarter, setQuarter] = useState(
    QUARTER_OPTIONS[0]?.value ?? "Q3_2022",
  );
  const [loading, setLoading] = useState(false);

  const handleGenerate = async () => {
    try {
      setLoading(true);
      const blob = await new FinanceService().generateVat100(quarter);
      const url = URL.createObjectURL(blob);
      Object.assign(document.createElement("a"), {
        href: url,
        download: `VAT100_${quarter}.pdf`,
      }).click();
      URL.revokeObjectURL(url);
      enqueueSnackbar(`VAT100 downloaded for ${quarter}`);
      onGenerateVat100();
    } catch {
      enqueueSnackbar("Failed to generate VAT100", { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <ReportCard
      title="VAT100 Return"
      description="HMRC VAT return with partial exemption working papers"
      icon="eva:file-text-fill"
      color="success">
      <TextField
        select
        size="small"
        fullWidth
        label="Quarter"
        value={quarter}
        onChange={(e) => setQuarter(e.target.value)}
        sx={{ mb: 1.5 }}>
        {QUARTER_OPTIONS.map((q) => (
          <MenuItem key={q.value} value={q.value}>
            {q.label}
          </MenuItem>
        ))}
      </TextField>
      <Button
        variant="contained"
        color="success"
        fullWidth
        disabled={loading}
        startIcon={
          loading ? (
            <CircularProgress size={14} color="inherit" />
          ) : (
            <Iconify icon="eva:download-fill" />
          )
        }
        onClick={handleGenerate}>
        {loading ? "Generating…" : "Download VAT100 PDF"}
      </Button>
      <Typography
        variant="caption"
        color="text.disabled"
        sx={{ mt: 1, display: "block", textAlign: "center" }}>
        Downloads a formatted VAT100 PDF with PE working papers
      </Typography>
    </ReportCard>
  );
}

// ── Xero card ──────────────────────────────────────────────────────────────────
function XeroExportCard() {
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<{
    transactionsCount: number;
    exportedAt: string;
  } | null>(null);

  const handlePush = async () => {
    try {
      setLoading(true);
      const res = await new FinanceService().exportToXero();
      setResult(res.data);
      enqueueSnackbar(`${res.data.transactionsCount} journals pushed to Xero!`);
    } catch {
      enqueueSnackbar("Xero export failed — check API connection", {
        variant: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <ReportCard
      title="Push to Xero"
      description="Export reconciled journals via Xero API"
      icon="eva:cloud-upload-fill"
      color="primary">
      <Alert severity="info" sx={{ mb: 1.5, borderRadius: 1, fontSize: 12 }}>
        Pushes all <strong>reconciled</strong> transactions as journal entries.
        Requires Xero OAuth — configure in Settings → Integrations.
      </Alert>
      <Button
        variant="contained"
        color="primary"
        fullWidth
        disabled={loading}
        startIcon={
          loading ? (
            <CircularProgress size={14} color="inherit" />
          ) : (
            <Iconify icon="eva:cloud-upload-fill" />
          )
        }
        onClick={handlePush}>
        {loading ? "Pushing…" : "Push Journals to Xero"}
      </Button>
      {result && (
        <Stack sx={{ mt: 1.5 }} spacing={0.5}>
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="caption" color="text.secondary">
              Journals pushed
            </Typography>
            <Typography
              variant="caption"
              fontFamily="monospace"
              fontWeight={600}>
              {result.transactionsCount}
            </Typography>
          </Stack>
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="caption" color="text.secondary">
              Exported at
            </Typography>
            <Typography variant="caption" fontFamily="monospace">
              {fDateTime(result.exportedAt)}
            </Typography>
          </Stack>
        </Stack>
      )}
    </ReportCard>
  );
}

// ── Reconciliation CSV card ────────────────────────────────────────────────────
function ReconciliationExportCard() {
  const { enqueueSnackbar } = useSnackbar();
  const [dateFrom, setDateFrom] = useState<Date | null>(null);
  const [dateTo, setDateTo] = useState<Date | null>(null);
  const [loading, setLoading] = useState(false);

  const handleExport = async () => {
    try {
      setLoading(true);
      // In production this calls a dedicated endpoint that returns CSV blob.
      // For now we fetch the P&L data and convert it.
      const svc = new FinanceService();
      const res = await svc.generatePlReport({
        dateFrom: dateFrom?.toISOString().split("T")[0] ?? "",
        dateTo: dateTo?.toISOString().split("T")[0] ?? "",
      });
      // Build simple CSV from P&L data
      const rows = [
        ["Category", "Type", "Income", "Expenditure", "Transactions"],
        ...(res.data.income ?? []).map((r: any) => [
          r.categoryName,
          "income",
          r.totalIncome,
          "0",
          r.transactionCount,
        ]),
        ...(res.data.expenditure ?? []).map((r: any) => [
          r.categoryName,
          "expenditure",
          "0",
          r.totalExpenditure,
          r.transactionCount,
        ]),
      ];
      const csv = rows.map((r) => r.join(",")).join("\n");
      const blob = new Blob([csv], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      Object.assign(document.createElement("a"), {
        href: url,
        download: `reconciliation_log_${dateFrom?.toISOString().split("T")[0]}.csv`,
      }).click();
      URL.revokeObjectURL(url);
      enqueueSnackbar("Reconciliation log exported!");
    } catch {
      enqueueSnackbar("Failed to export log", { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <ReportCard
      title="Reconciliation Log"
      description="Full audit export — matches, overrides, errors"
      icon="eva:checkmark-square-2-fill"
      color="warning">
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <Stack direction="row" spacing={1.5} mb={1.5}>
          <DatePicker
            label="From"
            value={dateFrom}
            onChange={setDateFrom}
            slotProps={{ textField: { size: "small", fullWidth: true } }}
          />
          <DatePicker
            label="To"
            value={dateTo}
            minDate={dateFrom ?? undefined}
            onChange={setDateTo}
            slotProps={{ textField: { size: "small", fullWidth: true } }}
          />
        </Stack>
      </LocalizationProvider>
      <Button
        variant="contained"
        color="warning"
        fullWidth
        disabled={!dateFrom || !dateTo || loading}
        startIcon={
          loading ? (
            <CircularProgress size={14} color="inherit" />
          ) : (
            <Iconify icon="eva:download-fill" />
          )
        }
        onClick={handleExport}>
        {loading ? "Exporting…" : "Export CSV"}
      </Button>
    </ReportCard>
  );
}

// ── Export history ─────────────────────────────────────────────────────────────
function ExportHistoryTable() {
  const { enqueueSnackbar } = useSnackbar();
  const [history, setHistory] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      const res = await new FinanceService().getExportHistory();
      const data = Array.isArray(res.data)
        ? res.data
        : Array.isArray(res.data?.data)
          ? res.data.data
          : [];
      setHistory(data);
    } catch {
      enqueueSnackbar("Failed to load export history", { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  return (
    <Stack spacing={1}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography variant="subtitle2">Import / export history</Typography>
        <Button
          size="small"
          startIcon={<Iconify icon="eva:refresh-fill" />}
          onClick={load}>
          Refresh
        </Button>
      </Stack>

      {loading ? (
        <Box sx={{ textAlign: "center", py: 3 }}>
          <CircularProgress size={24} />
        </Box>
      ) : history.length === 0 ? (
        <Card sx={{ py: 4, textAlign: "center" }}>
          <Typography variant="body2" color="text.disabled">
            No import/export history yet
          </Typography>
        </Card>
      ) : (
        <Card>
          <Scrollbar>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>File / Job</TableCell>
                  <TableCell>Format</TableCell>
                  <TableCell align="center">Rows imported</TableCell>
                  <TableCell align="center">Skipped</TableCell>
                  <TableCell align="center">Errors</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Date</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {history.map((item: any) => (
                  <TableRow key={item.id} hover>
                    <TableCell>
                      <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                        {item.filename}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={(item.format ?? "csv").toUpperCase()}
                        size="small"
                        variant="soft"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Typography
                        variant="body2"
                        fontFamily="monospace"
                        color="success.dark">
                        {item.importedRows ?? 0}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography
                        variant="body2"
                        fontFamily="monospace"
                        color="text.secondary">
                        {item.skippedRows ?? 0}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography
                        variant="body2"
                        fontFamily="monospace"
                        color={
                          item.errorRows > 0 ? "error.dark" : "text.secondary"
                        }>
                        {item.errorRows ?? 0}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={item.status ?? "pending"}
                        size="small"
                        color={
                          item.status === "completed"
                            ? "success"
                            : item.status === "failed"
                              ? "error"
                              : "warning"
                        }
                        variant="soft"
                        sx={{ textTransform: "capitalize" }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {fDate(item.createdAt)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Scrollbar>
        </Card>
      )}
    </Stack>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────
interface Props {
  onGenerateVat100: () => void;
}

export default function ExportsReportsPanel({ onGenerateVat100 }: Props) {
  return (
    <Stack spacing={3}>
      <Typography variant="subtitle1" fontWeight={500}>
        Exports & Reports
      </Typography>

      {/* ── 4 report cards ── */}
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <PlReportCard />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Vat100Card onGenerateVat100={onGenerateVat100} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <XeroExportCard />
        </Grid>
        <Grid item xs={12} sm={6}>
          <ReconciliationExportCard />
        </Grid>
      </Grid>

      {/* ── History ── */}
      <ExportHistoryTable />
    </Stack>
  );
}
